import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { AccountTab } from '../types';

// ========================================
// Styled Components - Matching Semantic UI tabular menu
// ========================================

const TabMenu = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(34, 36, 38, 0.15);
  margin-bottom: 0;
`;

const TabItem = styled(Link)<{ $active?: boolean }>`
  padding: 12px 20px;
  margin-bottom: -1px;
  background: ${props => props.$active ? '#fff' : 'transparent'};
  border: 1px solid transparent;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  color: ${props => props.$active ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.6)'};
  font-size: 14px;
  font-weight: ${props => props.$active ? '600' : '400'};
  text-decoration: none;
  cursor: pointer;
  transition: background 0.1s ease, color 0.1s ease;

  ${props => props.$active && `
    border-color: rgba(34, 36, 38, 0.15);
    border-bottom-color: #fff;
  `}

  &:hover {
    background: ${props => props.$active ? '#fff' : 'rgba(0, 0, 0, 0.03)'};
    color: rgba(0, 0, 0, 0.87);
  }
`;

// ========================================
// Tab Configuration
// ========================================

interface TabConfig {
  id: AccountTab;
  label: string;
  getPath: (accountId: string) => string;
}

const TABS: TabConfig[] = [
  {
    id: 'account-info',
    label: 'Account info',
    getPath: (accountId) => `/admin2/accounts/${accountId}/edit`,
  },
  {
    id: 'sso-saml',
    label: 'SSO/SAML settings',
    getPath: (accountId) => `/admin2/accounts/${accountId}/saml`,
  },
  {
    id: 'audit-events',
    label: 'Audit events',
    getPath: (accountId) => `/admin2/accounts/${accountId}/audit_events`,
  },
  {
    id: 'bulk-user-upload',
    label: 'Bulk user upload',
    getPath: (accountId) => `/admin2/accounts/${accountId}/bulk_user_uploads/new`,
  },
  {
    id: 'properties',
    label: 'Properties',
    getPath: (accountId) => `/admin2/accounts/${accountId}/properties`,
  },
];

// ========================================
// Component Props
// ========================================

interface AccountNavigationTabsProps {
  accountId: string;
  activeTab?: AccountTab;
}

// ========================================
// Component
// ========================================

/**
 * AccountNavigationTabs - Tab navigation for account pages
 * 
 * Migrated from: contently-contently/app/views/admin2/shared/_account_navigation_tabs.html.haml
 * 
 * Renders a tabular menu with links to each account sub-page.
 */
function AccountNavigationTabs({ accountId, activeTab }: AccountNavigationTabsProps) {
  const location = useLocation();
  
  // Determine active tab from URL if not explicitly provided
  const getActiveTab = (): AccountTab => {
    if (activeTab) return activeTab;
    
    const path = location.pathname;
    if (path.includes('/saml')) return 'sso-saml';
    if (path.includes('/audit_events')) return 'audit-events';
    if (path.includes('/bulk_user_uploads')) return 'bulk-user-upload';
    if (path.includes('/properties')) return 'properties';
    return 'account-info';
  };
  
  const currentTab = getActiveTab();

  return (
    <TabMenu>
      {TABS.map(tab => (
        <TabItem
          key={tab.id}
          to={tab.getPath(accountId)}
          $active={currentTab === tab.id}
        >
          {tab.label}
        </TabItem>
      ))}
    </TabMenu>
  );
}

export default AccountNavigationTabs;
